import type { CSSProperties, FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { _isEmpty } from '@/littledash';
import { capitalise } from '@/helpers';

import type { ID } from '@/model/Common.model';

import SortableItem from '@/components/Draggable/SortableItem';
import SortableList from '@/components/Draggable/SortableList';
import SearchSelect from '@/components/UI/SearchSelect';

const selectionEqual = (left: Array<ID>, right: Array<ID>): boolean =>
  Array.isArray(left) &&
  Array.isArray(right) &&
  left.length === right.length &&
  left.every((val, index) => val === right[index]);

interface Option {
  id: ID;
  title: string;
}

interface SelectionReorderProps<VAL extends Option = Option> {
  options: Array<VAL>;
  selectionTitle: string;
  selection?: Array<Option['id']>;
  selectionChange: (selection: Array<Option['id']>) => void;
  style?: CSSProperties;
}

interface ReorderProps<VAL extends Option = Option> extends Pick<SelectionReorderProps<VAL>, 'options'> {
  orderChange: (idOrder: Array<Option['id']>) => void;
  title: string;
}

const SELECT_CONTAINER_OVERFLOW_HEIGHT = 350;
const REORDER_CONTAINER_OVERFLOW_HEIGHT = 458;

const Reorder: FC<ReorderProps> = ({ title, options, orderChange }) => {
  const [sortableOptions, setSortableOptions] = useState([...(options ?? [])]);
  useEffect(() => {
    setSortableOptions(options ?? []);
  }, [options]);

  const handleReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      setSortableOptions((prev) => {
        const reordered = [...(prev ?? [])];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(destinationIndex, 0, moved);
        orderChange(reordered.map((o) => o.id));
        return reordered;
      });
    },
    [orderChange]
  );

  return (
    <div
      className="relative overflow-auto"
      data-test-component="Reorder"
      data-test-element="container"
      data-testid="selection-reorder-container"
      style={{ height: REORDER_CONTAINER_OVERFLOW_HEIGHT }}
    >
      {_isEmpty(sortableOptions) ? (
        <div className="pa2 ma3 bg-near-white br2">
          <h3 className="f7 lh-title near-black pb2 fw4">{capitalise(title)}s will appear here</h3>
          <p className="f7 lh-copy dark-gray">
            Select {title} types from the list and confirm the order these will be collected for each animal
          </p>
        </div>
      ) : (
        <SortableList droppableId="reorder-options" onReorder={handleReorder}>
          <ul className="list ma0 ph3 pv2">
            {sortableOptions.map((option, index) => (
              <li className="justify-between flex" key={option.id}>
                <SortableItem
                  id={String(option.id)}
                  index={index}
                  showDraggableIcon
                  className="flex flex-row justify-between items-center w-100 hide-child pb2"
                >
                  <div
                    className="flex flex-row ma1 near-black f6 lh-title"
                    data-test-element="sortable-item"
                    data-test-key={`${option.id}`}
                  >
                    {option.title}
                  </div>
                </SortableItem>
              </li>
            ))}
          </ul>
        </SortableList>
      )}
    </div>
  );
};

export const SelectionReorder: FC<SelectionReorderProps> = ({
  selectionTitle,
  options,
  selection,
  selectionChange,
}) => {
  const optionMap = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const [selectedIds, setSelectedIds] = useState<Array<ID>>(selection ?? []);
  const selectedOptions = useMemo(() => {
    const opts = selectedIds.reduce<Array<Option>>((acc, selectedId) => {
      if (optionMap.has(selectedId)) {
        acc.push(optionMap.get(selectedId) as Option);
      }
      return acc;
    }, []);
    if (!selectionEqual(selectedIds, selection ?? [])) {
      selectionChange([...selectedIds]);
    }
    return opts;
  }, [selectedIds, optionMap, selectionChange]);

  useEffect(() => {
    setSelectedIds(selection ?? []);
  }, [selection, setSelectedIds]);

  return (
    <div className="flex">
      <div className="w-50 h-100 br b--moon-gray">
        <SearchSelect
          selected={selectedIds}
          setSelected={setSelectedIds}
          sections={[{ items: options }]}
          overflowHeight={SELECT_CONTAINER_OVERFLOW_HEIGHT}
        />
      </div>
      <div className="w-50 h-100">
        <Reorder title={selectionTitle} options={selectedOptions} orderChange={setSelectedIds} />
      </div>
    </div>
  );
};
